import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle,
  Send,
  Loader2,
  Trash2,
  ArrowLeft,
  Users,
  Crown,
  Sparkles,
  Dumbbell,
  X as XIcon,
  Plus,
  ImagePlus,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d`;
  } catch {
    return "";
  }
}

function convoTitle(c, meId) {
  if (c.type === "group") return c.name || "Group Chat";
  const other = (c.members || []).find((m) => m.user_id !== meId);
  return other?.name || "Direct Message";
}

function convoAvatar(c, meId) {
  if (c.type === "group") {
    return (
      <div className="w-12 h-12 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
        <Users size={20} />
      </div>
    );
  }
  const other = (c.members || []).find((m) => m.user_id !== meId);
  if (other?.picture) {
    return (
      <img
        src={other.picture}
        alt={other.name}
        className="w-12 h-12 object-contain bg-black border border-[#D4AF37]"
      />
    );
  }
  return (
    <div className="w-12 h-12 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center font-display text-xl text-[#D4AF37]">
      {(other?.name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, width, height } or null
  const fileInputRef = useRef(null);
  const [newOpen, setNewOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const scrollRef = useRef(null);

  const me = user?.user_id;

  const loadConvos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/conversations`);
      setConvos(data.conversations || []);
    } catch {
      // silent
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (cid) => {
    setLoadingThread(true);
    try {
      const { data } = await axios.get(
        `${API}/messages/conversations/${cid}/messages`
      );
      setMessages(data.messages || []);
      await axios.post(`${API}/messages/conversations/${cid}/read`);
      // refresh convo list to clear unread on this convo
      loadConvos();
    } catch (e) {
      toast.error("Couldn't load messages.");
    } finally {
      setLoadingThread(false);
    }
  }, [loadConvos]);

  useEffect(() => {
    loadConvos();
  }, [loadConvos]);

  // If URL has ?conv=convId, auto-open that conversation once convos load
  useEffect(() => {
    if (!convos.length || active) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const cid = params.get("conv");
      if (cid) {
        const target = convos.find((c) => c.conversation_id === cid);
        if (target) {
          openConvo(target);
        }
      }
    } catch {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convos]);

  // Poll every 15s while screen open
  useEffect(() => {
    const id = setInterval(() => {
      loadConvos();
      if (active?.conversation_id) {
        // Refresh thread quietly
        axios
          .get(`${API}/messages/conversations/${active.conversation_id}/messages`)
          .then(({ data }) => setMessages(data.messages || []))
          .catch(() => {});
      }
    }, 15000);
    return () => clearInterval(id);
  }, [loadConvos, active]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, active?.conversation_id]);

  const openConvo = async (c) => {
    setActive(c);
    setMessages([]);
    await loadThread(c.conversation_id);
  };

  const send = async () => {
    if (!active) return;
    if (!draft.trim() && !pendingImage) return;
    setSending(true);
    const text = draft.trim();
    const imgToSend = pendingImage;
    setDraft("");
    setPendingImage(null);
    try {
      const body = { text };
      if (imgToSend) {
        body.attachment = {
          kind: "image",
          payload: {
            url: imgToSend.dataUrl,
            width: imgToSend.width,
            height: imgToSend.height,
          },
        };
      }
      const { data } = await axios.post(
        `${API}/messages/conversations/${active.conversation_id}/messages`,
        body
      );
      setMessages((m) => [...m, data.message]);
      loadConvos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
      setDraft(text);
      setPendingImage(imgToSend);
    } finally {
      setSending(false);
    }
  };

  // Resize an image client-side to a max 1280px long edge + 0.85 JPEG quality.
  // Keeps payloads well under the 3 MB server cap while preserving plenty of detail.
  const handleImagePicked = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That's not an image file.");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image is too large (max 12 MB before compression).");
      return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });
      const MAX = 1280;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width >= height) {
          height = Math.round((MAX / width) * height);
          width = MAX;
        } else {
          width = Math.round((MAX / height) * width);
          height = MAX;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const out = canvas.toDataURL("image/jpeg", 0.85);
      if (out.length > 3_400_000) {
        toast.error("Image still too large after compression — try a smaller one.");
        return;
      }
      setPendingImage({ dataUrl: out, width, height });
    } catch (e) {
      toast.error("Could not read that image.");
    }
  };

  const clearConvo = async () => {
    if (!active) return;
    if (!window.confirm("Clear all messages in this conversation for you only? Other members will still see their history.")) return;
    try {
      await axios.delete(`${API}/messages/conversations/${active.conversation_id}`);
      setMessages([]);
      toast.success("Conversation cleared from your view.");
      loadConvos();
    } catch {
      toast.error("Clear failed.");
    }
  };

  const openNew = async () => {
    try {
      const { data } = await axios.get(`${API}/friends`);
      setFriends(data.friends || []);
      setNewOpen(true);
    } catch {
      toast.error("Couldn't load your friends.");
    }
  };

  if (!user) {
    return (
      <section className="py-20 bg-[#0A0A0A] min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <MessageCircle className="mx-auto text-[#D4AF37] mb-3" size={42} />
          <h2 className="font-display text-3xl text-white mb-2">Messages</h2>
          <p className="text-neutral-500 text-sm">
            Sign in to start a direct conversation with your inner circle.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="messages-page"
      className="pt-28 pb-16 bg-[#0A0A0A] min-h-[80vh]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Inner Circle · Direct
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white">
            Messages
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Private chats with your friends. Share workouts, recipes, and hype each other up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-0 border border-zinc-800 bg-[#0F0F0F] min-h-[60vh]">
          {/* Conversation list */}
          <aside
            data-testid="messages-list"
            className={`border-r border-zinc-800 ${active ? "hidden md:block" : ""}`}
          >
            <div className="flex items-center justify-between p-3 border-b border-zinc-900">
              <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
                Conversations
              </span>
              <button
                data-testid="messages-new-btn"
                onClick={openNew}
                className="w-8 h-8 bg-[#D4AF37] hover:bg-[#f5d96b] text-black flex items-center justify-center"
                title="Start a new chat"
              >
                <Plus size={14} strokeWidth={2.5} />
              </button>
            </div>
            {loadingList ? (
              <div className="text-center text-neutral-500 py-8 text-sm">
                <Loader2 className="animate-spin inline-block mr-2" size={14} />
                Loading…
              </div>
            ) : convos.length === 0 ? (
              <div className="text-center text-neutral-500 py-10 text-sm px-4">
                No chats yet. Click <Plus size={12} className="inline -mt-0.5" /> to start one
                with a friend.
              </div>
            ) : (
              <ul className="max-h-[70vh] overflow-y-auto">
                {convos.map((c) => {
                  const isActive = active?.conversation_id === c.conversation_id;
                  const title = convoTitle(c, me);
                  return (
                    <li key={c.conversation_id}>
                      <button
                        data-testid={`messages-convo-${c.conversation_id}`}
                        onClick={() => openConvo(c)}
                        className={`w-full flex gap-3 p-3 text-left border-b border-zinc-900 hover:bg-black/40 transition-colors ${
                          isActive ? "bg-[#D4AF37]/5 border-l-2 border-l-[#D4AF37]" : ""
                        }`}
                      >
                        {convoAvatar(c, me)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-display text-base text-white truncate">
                              {title}
                            </span>
                            <span className="text-[10px] text-neutral-500 flex-shrink-0">
                              {timeAgo(c.last_message_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-neutral-400 truncate flex-1">
                              {c.last_message_preview || "Start the conversation…"}
                            </span>
                            {c.unread > 0 && (
                              <span
                                data-testid={`messages-unread-${c.conversation_id}`}
                                className="bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none min-w-[18px] text-center"
                              >
                                {c.unread}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Thread */}
          <div
            className={`flex flex-col min-h-[60vh] ${active ? "" : "hidden md:flex"}`}
            data-testid="messages-thread"
          >
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-neutral-500 text-sm p-8 text-center">
                <div>
                  <MessageCircle size={42} className="mx-auto text-[#D4AF37]/40 mb-3" />
                  Pick a conversation to read or reply.
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-900">
                  <button
                    className="md:hidden text-neutral-400 hover:text-white"
                    onClick={() => setActive(null)}
                    data-testid="messages-back-btn"
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  {convoAvatar(active, me)}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-white truncate">
                      {convoTitle(active, me)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {active.type === "group"
                        ? `${(active.members || []).length} members`
                        : "Direct Message"}
                    </div>
                  </div>
                  <button
                    data-testid="messages-clear-btn"
                    onClick={clearConvo}
                    className="w-9 h-9 bg-[#0A0A0A] border border-zinc-700 hover:border-[#DC2626] text-neutral-400 hover:text-[#DC2626] flex items-center justify-center"
                    title="Clear my view of this chat"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Messages list */}
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-2 max-h-[60vh]"
                >
                  {loadingThread ? (
                    <div className="text-center text-neutral-500 py-6 text-sm">
                      <Loader2 className="animate-spin inline mr-2" size={14} />
                      Loading…
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-neutral-500 text-sm py-10">
                      No messages yet. Be the first to drop a hello 👋
                    </div>
                  ) : (
                    messages.map((m) => (
                      <MessageBubble
                        key={m.message_id}
                        m={m}
                        sender={(active.members || []).find((x) => x.user_id === m.sender_id)}
                        groupMode={active.type === "group"}
                        currentUserId={(user || {}).user_id}
                        onReact={async (emoji) => {
                          try {
                            const { data } = await axios.post(`${API}/messages/${m.message_id}/react`, { emoji });
                            setMessages((arr) =>
                              arr.map((x) => (x.message_id === m.message_id ? { ...x, reactions: data.reactions } : x))
                            );
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Reaction failed.");
                          }
                        }}
                      />
                    ))
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-zinc-900 p-3">
                  {/* Image preview chip */}
                  {pendingImage && (
                    <div
                      data-testid="messages-pending-image"
                      className="mb-2 inline-flex items-center gap-2 bg-[#0A0A0A] border border-[#D4AF37] p-1.5 pr-2"
                    >
                      <img
                        src={pendingImage.dataUrl}
                        alt=""
                        className="w-12 h-12 object-cover"
                      />
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                        Photo ready
                      </span>
                      <button
                        data-testid="messages-clear-image"
                        onClick={() => setPendingImage(null)}
                        className="text-neutral-500 hover:text-red-500"
                        title="Remove photo"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid="messages-file-input"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) handleImagePicked(f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      data-testid="messages-attach-image-btn"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      disabled={sending}
                      className="btn-omni btn-outline disabled:opacity-40 px-3"
                      title="Attach photo"
                    >
                      <ImagePlus size={14} />
                    </button>
                    <input
                      data-testid="messages-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      placeholder={pendingImage ? "Add a caption (optional)…" : "Type a message…"}
                      className="input-omni flex-1"
                      maxLength={2000}
                    />
                    <button
                      data-testid="messages-send-btn"
                      onClick={send}
                      disabled={sending || (!draft.trim() && !pendingImage)}
                      className="btn-omni btn-red disabled:opacity-40"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {newOpen && (
        <NewChatModal
          friends={friends}
          onClose={() => setNewOpen(false)}
          onCreated={async (cid) => {
            setNewOpen(false);
            await loadConvos();
            const fresh = await axios.get(`${API}/messages/conversations`);
            const c = (fresh.data.conversations || []).find((x) => x.conversation_id === cid);
            if (c) openConvo(c);
          }}
        />
      )}
    </section>
  );
}

function MessageBubble({ m, sender, groupMode, currentUserId, onReact }) {
  const mine = !!m.is_me;
  const att = m.attachment;
  const isImage = att?.kind === "image";
  return (
    <div
      data-testid={`message-${m.message_id}`}
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
    >
      <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {groupMode && !mine && sender && (
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-0.5 flex items-center gap-1">
            {sender.role === "owner" && <Crown size={9} />}
            {sender.is_premium && sender.role !== "owner" && <Sparkles size={9} />}
            {sender.name}
          </div>
        )}
        {m.text && (
          <div className="relative group">
            <div
              className={`px-3 py-2 ${
                mine
                  ? "bg-[#DC2626] text-white border border-[#DC2626]"
                  : "bg-[#0A0A0A] text-neutral-100 border border-zinc-800"
              } whitespace-pre-wrap break-words text-sm leading-snug`}
            >
              {m.text}
            </div>
            {!isImage && (
              <ReactionsOverlay
                messageId={m.message_id}
                reactions={m.reactions || {}}
                currentUserId={currentUserId}
                onReact={onReact}
                mine={mine}
              />
            )}
          </div>
        )}
        {att?.kind === "workout" && <WorkoutCard payload={att.payload} mine={mine} />}
        {att?.kind === "recipe" && <RecipeCard payload={att.payload} mine={mine} />}
        {isImage && (
          <ImageBubble
            payload={att.payload}
            mine={mine}
            messageId={m.message_id}
            reactions={m.reactions || {}}
            currentUserId={currentUserId}
            onReact={onReact}
          />
        )}
        <div
          className={`text-[10px] text-neutral-600 mt-0.5 ${mine ? "text-right" : "text-left"}`}
        >
          {timeAgo(m.created_at)} ago
        </div>
      </div>
    </div>
  );
}

function WorkoutCard({ payload, mine }) {
  const p = payload || {};
  return (
    <a
      href="/#workout-of-the-week"
      data-testid="msg-workout-card"
      className={`mt-1 block p-3 border-2 ${
        mine ? "border-[#D4AF37] bg-black/40" : "border-[#D4AF37] bg-[#0A0A0A]"
      } hover:bg-[#D4AF37]/5 transition-colors`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Dumbbell size={14} className="text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Workout
        </span>
      </div>
      <div className="font-display text-base text-white leading-tight">
        {p.title || "Workout"}
      </div>
      {p.tagline && (
        <div className="text-[11px] text-neutral-400 italic mt-1">"{p.tagline}"</div>
      )}
      <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase tracking-widest text-neutral-500">
        {p.focus && <span className="border border-zinc-700 px-1.5 py-0.5">{p.focus}</span>}
        {p.level && <span className="border border-zinc-700 px-1.5 py-0.5">{p.level}</span>}
        {p.days_per_week && (
          <span className="border border-zinc-700 px-1.5 py-0.5">{p.days_per_week}d/wk</span>
        )}
        {p.duration_min && (
          <span className="border border-zinc-700 px-1.5 py-0.5">{p.duration_min}m</span>
        )}
      </div>
    </a>
  );
}

function RecipeCard({ payload, mine }) {
  const p = payload || {};
  return (
    <a
      href="/#recipe-of-the-week"
      data-testid="msg-recipe-card"
      className={`mt-1 block p-3 border-2 border-[#D4AF37] bg-${
        mine ? "black/40" : "[#0A0A0A]"
      } hover:bg-[#D4AF37]/5 transition-colors`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[#D4AF37]">🍳</span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Recipe
        </span>
      </div>
      <div className="font-display text-base text-white leading-tight">
        {p.title || "Recipe"}
      </div>
      {p.tagline && (
        <div className="text-[11px] text-neutral-400 italic mt-1">"{p.tagline}"</div>
      )}
    </a>
  );
}

const REACTION_EMOJIS = ["🔥", "❤️", "💪", "🌈", "🥚"];

/**
 * ReactionsOverlay — shared picker shown on hover OR whenever a reaction
 * already exists on the message. Used by both ImageBubble (overlaid on the
 * photo) and the text-bubble wrapper in MessageBubble.
 */
function ReactionsOverlay({ messageId, reactions = {}, currentUserId, onReact, mine }) {
  const [showPicker, setShowPicker] = useState(false);
  const reactedEmojis = Object.entries(reactions).filter(([, ids]) => Array.isArray(ids) && ids.length > 0);
  if (!onReact) return null;
  const visible = showPicker || reactedEmojis.length > 0;
  if (!visible) {
    // Invisible hover-target so the picker can fade in on hover
    return (
      <div
        data-testid={`message-react-hover-${messageId}`}
        className="absolute -bottom-2 right-2 w-8 h-4 cursor-pointer"
        onMouseEnter={() => setShowPicker(true)}
      />
    );
  }
  const hasReacted = (emoji) =>
    Array.isArray(reactions[emoji]) && currentUserId && reactions[emoji].includes(currentUserId);
  return (
    <div
      data-testid={`message-react-picker-${messageId}`}
      onMouseEnter={() => setShowPicker(true)}
      onMouseLeave={() => setShowPicker(false)}
      className={`absolute -bottom-3 ${mine ? "right-2" : "left-2"} flex items-center gap-0.5 bg-[#0A0A0A] border border-zinc-800 shadow-lg px-1.5 py-1 z-10`}
    >
      {REACTION_EMOJIS.map((emoji) => {
        const count = (reactions[emoji] || []).length;
        const reacted = hasReacted(emoji);
        return (
          <button
            key={emoji}
            data-testid={`message-react-${emoji}-${messageId}`}
            onClick={(e) => {
              e.stopPropagation();
              onReact(emoji);
            }}
            className={`text-sm leading-none px-1.5 py-0.5 transition-colors ${
              reacted ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "hover:bg-zinc-900"
            }`}
            title={`React ${emoji}`}
          >
            <span>{emoji}</span>
            {count > 0 && <span className="ml-1 text-[10px] font-bold tabular-nums">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ImageBubble({ payload, mine, messageId, reactions = {}, currentUserId, onReact }) {
  const url = payload?.url;
  const [zoom, setZoom] = useState(false);
  if (!url) return null;
  // Aspect-ratio padding so the bubble doesn't jump when the image loads
  const ratio =
    payload.width && payload.height
      ? Math.max(0.4, Math.min(2.0, payload.height / payload.width))
      : 0.75;
  return (
    <>
      <div
        className="relative mt-1 inline-block group"
      >
        <button
          data-testid="message-image"
          onClick={() => setZoom(true)}
          className={`block max-w-[260px] border ${
            mine ? "border-[#DC2626]/60" : "border-zinc-800"
          } overflow-hidden hover:opacity-90 transition-opacity`}
          style={{ width: 260 }}
          title={payload.alt || "Photo"}
        >
          <div className="relative bg-black" style={{ paddingBottom: `${ratio * 100}%` }}>
            <img
              src={url}
              alt={payload.alt || "Photo"}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </button>
        <ReactionsOverlay
          messageId={messageId}
          reactions={reactions}
          currentUserId={currentUserId}
          onReact={onReact}
          mine={mine}
        />
      </div>
      {zoom && (
        <div
          data-testid="message-image-zoom"
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setZoom(false)}
        >
          <img
            src={url}
            alt={payload.alt || "Photo"}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            data-testid="message-image-zoom-close"
            onClick={() => setZoom(false)}
            className="absolute top-6 right-6 text-white/80 hover:text-white"
          >
            <XIcon size={28} />
          </button>
        </div>
      )}
    </>
  );
}



function NewChatModal({ friends, onClose, onCreated }) {
  const [selected, setSelected] = useState(new Set());
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isGroup = selected.size >= 2;

  const create = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one friend.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/messages/conversations`, {
        type: isGroup ? "group" : "dm",
        member_ids: Array.from(selected),
        name: isGroup ? groupName.trim() || null : null,
      });
      onCreated?.(data.conversation_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="messages-new-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              New Chat
            </div>
            <h3 className="font-display text-2xl text-white">
              {isGroup ? "Group" : "Direct Message"}
            </h3>
          </div>
          <button
            data-testid="messages-new-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
          >
            <XIcon size={22} />
          </button>
        </div>
        {friends.length === 0 ? (
          <p className="text-sm text-neutral-500 py-8 text-center">
            Add friends from the Friends tab first.
          </p>
        ) : (
          <>
            <p className="text-[11px] text-neutral-500 mb-3">
              {isGroup
                ? `Group with ${selected.size + 1} members (incl. you).`
                : "Select 1 friend for a DM, or 2+ for a group chat."}
            </p>
            {isGroup && (
              <input
                data-testid="messages-new-group-name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Group name (optional)"
                className="input-omni w-full mb-3"
                maxLength={60}
              />
            )}
            <div className="space-y-2 mb-4 max-h-[40vh] overflow-y-auto">
              {friends.map((f) => (
                <button
                  key={f.user_id}
                  data-testid={`messages-new-friend-${f.user_id}`}
                  onClick={() => toggle(f.user_id)}
                  className={`w-full flex items-center gap-3 p-2 border ${
                    selected.has(f.user_id)
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  {f.picture ? (
                    <img
                      src={f.picture}
                      alt={f.name}
                      className="w-9 h-9 object-contain bg-black border border-[#D4AF37]"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center font-display text-base text-[#D4AF37]">
                      {(f.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="flex-1 text-left font-display text-sm text-white">
                    {f.full_name || f.name}
                  </span>
                  {selected.has(f.user_id) && (
                    <span className="text-[#D4AF37] text-xs">✓</span>
                  )}
                </button>
              ))}
            </div>
            <button
              data-testid="messages-new-create"
              onClick={create}
              disabled={busy || selected.size === 0}
              className="btn-omni btn-red w-full disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              Start {isGroup ? "Group" : "Chat"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
